/**
 * Alle Texte der Oberfläche. Zunächst nur Deutsch (E-25); weitere Sprachen bekommen ein Objekt
 * mit derselben Form.
 */
export const de = {
  appName: "numbet",
  heroTitle: "Schätzfragen mit Pokerspannung.",
  heroSub: "Tippe eine Zahl, setze wie beim Poker und gewinne den Pot, wenn du am nächsten dran bist.",
  footerFacts: "2 bis 8 Personen · Chips ohne Geldwert · kein Glücksspiel",

  // Startseite, Raum erstellen und beitreten (4a–4c)
  createRoom: "Raum erstellen",
  joinRoom: "Raum beitreten",
  or: "oder",
  roomCode: "Raumcode",
  roomCodePlaceholder: "Raumcode, z. B. ZUG42K",
  join: "Beitreten",
  howItWorks: "So funktioniert's",
  back: "Zurück",
  createIntro: "Wähle einen Nicknamen und optional einen Avatar. Den Raumcode bekommst du im nächsten Schritt.",
  joinIntro: "Gib den Raumcode ein, den dir der Host schickt.",
  joinIntroLink: "Du hast einen Einladungslink geöffnet. Der Code ist schon eingetragen, es fehlt nur dein Nickname.",
  codeFromLink: "Code aus dem Einladungslink übernommen",
  codeUnknown: "Diesen Raum gibt es nicht. Prüfe den Code.",
  insteadJoin: "Stattdessen Raum beitreten",
  insteadCreate: "Stattdessen Raum erstellen",
  nickname: "Nickname",
  nicknamePlaceholder: "Dein Nickname",
  avatarOptional: "Avatar (optional)",
  avatarDefault: "Standard: Initialen",
  avatarNames: {
    initials: "Initialen",
    circle: "Kreis",
    triangle: "Dreieck",
    diamond: "Raute",
    hexagon: "Sechseck",
    plus: "Plus",
    ring: "Ring",
    dome: "Halbkreis",
  } as Record<string, string>,
  yourSeat: "Dein Platz am Tisch",
  yourRoom: "Dein Raum",
  statQuestions: "Fragen",
  statBlinds: "Blinds",
  chips: "Chips",
  defaultsInfo: "10 Fragen · 1.000 Chips · Blinds 10/20",
  noMoney: "Kein Glücksspiel: Chips haben keinen Geldwert.",
  demoQuestion: "Wie hoch ist die Zugspitze?",
  demoHints: ["Die Antwort ist vierstellig.", "Die Zugspitze ist niedriger als der Großglockner (3.798 m).", "Sie ist höher als 2.500 m."],

  // Lobby
  lobbyTitle: "Warteraum",
  shareHint: "Teile den Link oder den Code mit deinen Mitspielern.",
  copyLink: "Link kopieren",
  copied: "Kopiert!",
  share: "Teilen",
  players: (n: number, max: number) => `Spieler (${n}/${max})`,
  host: "Host",
  you: "du",
  freeSeat: "freier Platz",
  startGame: "Spiel starten",
  needPlayers: "Mindestens 2 Spieler nötig",
  waitingForHost: "Warte auf den Host …",
  leaveRoom: "Raum verlassen",
  joinThisRoom: (code: string) => `Raum ${code} beitreten`,
  gameRunning: "In diesem Raum läuft bereits eine Partie.",
  roomGone: "Diesen Raum gibt es nicht (mehr).",
  toHome: "Zur Startseite",

  // Spieltisch
  handProgress: (n: number, total: number) => `Frage ${n} von ${total}`,
  blinds: (sb: number, bb: number) => `Blinds ${sb}/${bb}`,
  pot: "Pot",
  questionPill: (n: number, total: number) => `Frage ${n} / ${total}`,
  answerIn: (unit: string) => `Antwort in ${unit}`,
  hintPill: (i: number) => `Hinweis ${i}/3`,
  yourTurnPill: (time: string) => `Du bist dran · ${time}`,
  playerTurnPill: (name: string, time: string) => `${name} ist am Zug · ${time}`,
  yourTip: "Dein Tipp",
  logTitle: (hand: number) => `Verlauf · Hand ${hand}`,
  statusWaiting: "wartet",
  statusTurn: (time: string) => `am Zug · ${time}`,
  stake: (amount: string) => `Einsatz ${amount}`,
  adjust: "Betrag anpassen",
  sidePot: (i: number) => `Side Pot ${i}`,
  hint: (i: number) => `Hinweis ${i}`,
  hiddenHint: "verdeckt",
  answer: "Antwort",
  hiddenAnswer: "?",
  yourEstimate: "Dein Tipp",
  estimateLocked: "Dein Tipp ist fix. Jetzt wird gepokert.",
  submitEstimate: "Tipp abgeben",
  estimateFinal: "Danach nicht mehr änderbar.",
  estimateInvalid: "Bitte eine gültige Zahl eingeben.",
  estimateDecimals: (d: number) => (d === 0 ? "Bitte eine ganze Zahl eingeben." : `Höchstens ${d} Nachkommastellen.`),
  beforeChrist: "v. Chr.",
  waitingForEstimates: "Warte auf die anderen Schätzungen …",
  deviation: (amount: string, direction: "high" | "low" | "exact") =>
    direction === "exact" ? "Volltreffer!" : `Du liegst ${amount} daneben, ${direction === "high" ? "zu hoch" : "zu niedrig"}.`,
  yourTurn: "Du bist dran",
  toCall: (amount: string) => `${amount} zum Mitgehen`,
  yourTurnCall: (amount: string) => `Du bist dran: ${amount} zum Mitgehen.`,
  yourTurnCheck: "Du bist dran.",
  playerTurn: (name: string) => `${name} ist am Zug`,
  fold: "Aussteigen",
  check: "Schieben",
  call: (amount: string) => `Mitgehen ${amount}`,
  bet: "Setzen",
  raise: "Erhöhen",
  betTo: (amount: string) => `Setzen ${amount}`,
  raiseTo: (amount: string) => `Erhöhen auf ${amount}`,
  allIn: "Alles setzen",
  min: "Min",
  halfPot: "½ Pot",
  fullPot: "Pot",
  cancel: "Abbrechen",
  amount: "Betrag",
  estimated: "hat geschätzt",
  folded: "ausgestiegen",
  allInStatus: "alles gesetzt",
  sittingOut: "pausiert",
  offline: "offline",
  eliminated: "ausgeschieden",
  dealer: "Dealer",
  smallBlind: "Small Blind",
  bigBlind: "Big Blind",
  waitingForPlayers: "Warte auf Spieler – zu wenige sind bereit.",
  sitOut: "Pause machen",
  sitIn: "Weiterspielen",
  youSitOut: "Du machst Pause und wirst nicht ausgeteilt.",
  menu: "Menü",
  log: "Verlauf",
  connecting: "Verbinde …",
  reconnecting: "Verbindung unterbrochen – verbinde neu …",
  replaced: "Dieser Raum ist in einem anderen Tab geöffnet.",
  reloadHere: "Hier weiterspielen",

  // Ergebnis
  wins: "gewinnt",
  winsPot: (name: string, amount: string) => `${name} gewinnt ${amount} Chips`,
  winsUncontested: (name: string, amount: string) => `${name} gewinnt ${amount} Chips – alle anderen sind ausgestiegen.`,
  splitPot: (names: string, amount: string) => `${names} teilen sich ${amount} Chips`,
  noContest: "Niemand hat geschätzt – alle bekommen ihren Einsatz zurück.",
  theAnswerWas: "Die Antwort",
  showEstimate: "Meine Schätzung zeigen",
  nextHandIn: (s: number) => `Nächste Hand in ${s} s`,
  finalResultIn: (s: number) => `Auswertung in ${s} s`,
  and: "und",

  // Spielende
  gameOver: "Spielende",
  winner: (names: string) => `${names} gewinnt!`,
  winners: (names: string) => `${names} gewinnen!`,
  place: (rank: number) => `${rank}.`,
  highlights: "Highlights der Partie",
  bestEstimate: "Beste Schätzung",
  biggestPot: "Größter Pot",
  mostHands: "Meiste Hände gewonnen",
  handsWon: (n: number) => (n === 1 ? "1 Hand" : `${n} Hände`),
  playAgain: "Nochmal spielen",
  waitingForRematch: "Warte auf den Host …",

  // Ereignisse im Verlauf
  ev: {
    handStarted: (n: number) => `Frage ${n} beginnt`,
    blind: (name: string, amount: string) => `${name} zahlt den Blind (${amount})`,
    estimated: (name: string) => `${name} hat geschätzt`,
    fold: (name: string) => `${name} steigt aus`,
    check: (name: string) => `${name} schiebt`,
    call: (name: string, amount: string) => `${name} geht mit (${amount})`,
    bet: (name: string, amount: string) => `${name} setzt ${amount}`,
    raise: (name: string, amount: string) => `${name} erhöht auf ${amount}`,
    allIn: (name: string, amount: string) => `${name} setzt alles (${amount})`,
    timedOut: (name: string) => `${name}: Zeit abgelaufen`,
    hint: (i: number) => `Hinweis ${i} aufgedeckt`,
    answer: (value: string) => `Antwort: ${value}`,
    returned: (name: string, amount: string) => `${name} bekommt ${amount} zurück`,
    showdown: "Showdown!",
    shown: (name: string) => `${name} zeigt die Schätzung`,
    satOut: (name: string) => `${name} macht Pause`,
    satIn: (name: string) => `${name} ist zurück`,
    left: (name: string) => `${name} hat den Raum verlassen`,
    joined: (name: string) => `${name} ist beigetreten`,
    eliminated: (name: string) => `${name} ist ausgeschieden`,
  },

  // Regeln
  rulesTitle: "So funktioniert numbet",
  rules: [
    ["Schätzen", "Alle bekommen dieselbe Schätzfrage. Jeder gibt verdeckt eine Zahl ab – das ist deine Hand. Ändern kannst du sie danach nicht mehr."],
    ["Hinweise", "Nacheinander werden drei Hinweise aufgedeckt. Sie grenzen die Antwort ein – was sie für deine Schätzung bedeuten, musst du selbst herausfinden."],
    ["Setzen", "Vor dem ersten und nach jedem Hinweis wird gesetzt wie beim Texas Hold'em: Schieben, Mitgehen, Erhöhen oder Aussteigen. Bluffen ausdrücklich erlaubt."],
    ["Auflösung", "Dann wird die Antwort aufgedeckt. Du weißt jetzt genau, wie weit du daneben liegst – aber nicht, wie weit die anderen danebenliegen. Letzte Setzrunde!"],
    ["Showdown", "Die Schätzungen werden aufgedeckt. Wer am nächsten dran ist, gewinnt den Pot. Nach 10 Fragen gewinnt, wer die meisten Chips hat."],
  ] as [string, string][],

  legal: {
    imprint: "Impressum",
    terms: "Nutzungsbedingungen",
    privacy: "Datenschutz",
    onThisPage: "Auf dieser Seite",
    asOf: "Stand",
  },

  // Einwilligung in die Nutzungsstatistik (E-33)
  consent: {
    title: "Darf numbet mitzählen?",
    text: "numbet würde gern zählen, wie das Spiel genutzt wird: aufgerufene Seiten, Räume und Partien. Dafür läuft eine eigene Matomo-Installation, ohne Cookies, ohne Wiedererkennung und ohne vollständige IP-Adresse. Ohne deine Zustimmung wird nichts erfasst.",
    browserOptOut: "Dein Browser sendet „Do Not Track“ oder „Global Privacy Control“. Deshalb erfasst numbet nichts.",
    more: "Mehr in der Datenschutzerklärung",
    allow: "Zustimmen",
    deny: "Ablehnen",
    ok: "Verstanden",
    currentGranted: "Aktuell: Du hast zugestimmt.",
    currentDenied: "Aktuell: Du hast abgelehnt.",
    settings: "Statistik-Einstellungen",
  },

  // Admin-Backend
  admin: {
    title: "Fragenverwaltung",
    login: "Anmelden",
    logout: "Abmelden",
    username: "Benutzername",
    password: "Passwort",
    questions: "Fragen",
    tags: "Tags",
    newQuestion: "Neue Frage",
    search: "Suche",
    searchPlaceholder: "Frage oder ID …",
    status: "Status",
    all: "Alle",
    active: "Aktiv",
    draft: "Entwurf",
    tag: "Tag",
    allTags: "Alle Tags",
    question: "Frage",
    unit: "Einheit",
    answer: "Antwort",
    decimals: "Nachkommastellen",
    hint: (i: number) => `Hinweis ${i}`,
    hintHelp: ["grob – z. B. Größenordnung", "mittel – z. B. Vergleich mit Bekanntem", "fein – grenzt weiter ein, verrät aber nichts"],
    source: "Quelle (URL)",
    asOf: "Stichtag (für Werte, die sich ändern)",
    difficulty: "Schwierigkeit",
    easy: "leicht",
    medium: "mittel",
    hard: "schwer",
    changed: "Geändert",
    count: (n: number) => (n === 1 ? "1 Frage" : `${n} Fragen`),
    stats: (active: number, draft: number) => `${active} aktiv · ${draft} Entwurf`,
    selected: (n: number) => `${n} ausgewählt`,
    addTag: "Tag hinzufügen",
    removeTag: "Tag entfernen",
    tagPlaceholder: "Tag eingeben, Enter",
    save: "Speichern",
    saved: "Gespeichert",
    delete: "Löschen",
    confirmDelete: "Diese Frage wirklich löschen?",
    back: "Zur Liste",
    preview: "Vorschau",
    previewStep: (step: number) => ["Nur die Frage", "Hinweis 1", "Hinweis 2", "Hinweis 3", "Auflösung"][step] ?? "",
    previous: "Zurück",
    next: "Weiter",
    rename: "Umbenennen",
    mergeInto: "Zusammenführen mit …",
    confirmDeleteTag: (name: string) => `Tag „${name}“ löschen? Die Fragen bleiben erhalten.`,
    noResults: "Keine Fragen gefunden.",
    answerDecimals: "Die Antwort hat mehr Nachkommastellen als erlaubt.",
    page: (from: number, to: number, total: number) => `${from}–${to} von ${total}`,
  },

  // Fehler
  errors: {
    notHost: "Nur der Host kann das.",
    notEnoughPlayers: "Es braucht mindestens 2 Spieler.",
    notEnoughQuestions: "Es gibt gerade nicht genug Fragen.",
    roomFull: "Der Raum ist voll.",
    roomNotFound: "Diesen Raum gibt es nicht.",
    gameAlreadyStarted: "Die Partie hat schon begonnen.",
    invalidName: "Bitte einen Namen mit 1 bis 24 Zeichen eingeben.",
    invalidEstimate: "Diese Schätzung ist ungültig.",
    estimateLocked: "Deine Schätzung steht schon fest.",
    notYourTurn: "Du bist gerade nicht dran.",
    betTooSmall: "Der Einsatz ist zu klein.",
    raiseNotAllowed: "Du darfst gerade nicht erhöhen.",
    insufficientChips: "So viele Chips hast du nicht.",
    tooManyRooms: "Gerade sind zu viele Räume offen. Bitte später noch einmal versuchen.",
    network: "Keine Verbindung zum Server.",
    invalidCredentials: "Benutzername oder Passwort ist falsch.",
    tooManyAttempts: "Zu viele Versuche. Bitte eine Minute warten.",
    unauthorized: "Bitte erneut anmelden.",
    invalidRequest: "Die Eingaben sind unvollständig oder ungültig.",
    notFound: "Nicht gefunden.",
    unknown: "Da ist etwas schiefgelaufen.",
  } as Record<string, string>,
};

export type Messages = typeof de;
export const t: Messages = de;

export function errorText(code: string): string {
  return t.errors[code] ?? t.errors.unknown ?? code;
}

/** „Anna, Ben und Cem“ */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} ${t.and} ${names.at(-1)}`;
}
